import React from 'react';
import DOMPurify from 'dompurify';

/**
 * Minimal markdown renderer for Maestra legal pages.
 *
 * Intentionally a tiny purpose-built subset, not a full markdown engine —
 * keeps the bundle clean and the output strictly editorial.
 *
 * Supported:
 *   ## Section heading     → Playfair h2
 *   blank-line paragraphs  → calm reading paragraphs
 *   > callout text         → italic Cormorant callout box
 *   ---                    → soft divider
 *   **bold**, *italic*     → inline emphasis
 *   [text](url)            → links (mailto: included)
 *   trailing "  " on line  → hard line break
 *
 * Anything we don't recognise renders as plain text, which is fine — admins
 * have a live preview alongside the editor, so they see the result immediately.
 *
 * Security: user input is first HTML-escaped (`esc`), then transformed into a
 * tiny whitelist of inline tags, and finally passed through DOMPurify as a
 * defense-in-depth pass. The sanitizer only allows the same tag/attribute set
 * we emit ourselves, so it never strips or escapes legitimate markup, but it
 * does block anything that somehow slipped through (e.g. a future regex bug).
 */

const URL_RE = /\[([^\]]+)\]\(([^)]+)\)/g;
const BOLD_RE = /\*\*([^*]+)\*\*/g;
const ITALIC_RE = /\*([^*]+)\*/g;

// Escape any HTML special chars first so user input can't inject tags.
const esc = (s) => String(s)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;');

// DOMPurify config — only the tiny tag/attribute set this renderer can emit.
// Setting this explicitly avoids accidental drift if DOMPurify's defaults
// change in a future release.
const PURIFY_CONFIG = {
  ALLOWED_TAGS: ['a', 'strong', 'em', 'br'],
  ALLOWED_ATTR: ['href'],
  // Hard-block any URL scheme other than http(s), mailto, or same-origin paths.
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|\/)/i,
};

// Single sanitization pass over already-rendered HTML.
// IMPORTANT: do not call `esc()` on the output — content is already escaped
// (or known-safe markup we generated). Calling esc twice would produce visible
// "&amp;lt;" garbage and break line breaks, links, and multilingual chars.
const sanitize = (html) => DOMPurify.sanitize(html, PURIFY_CONFIG);

const renderInline = (text) => {
  let html = esc(text);
  // Links — guard against javascript: URLs.
  html = html.replace(URL_RE, (_, label, url) => {
    const safe = /^(https?:|mailto:|\/)/i.test(url) ? url : '#';
    return `<a href="${safe}">${label}</a>`;
  });
  html = html.replace(BOLD_RE, (_, s) => `<strong>${s}</strong>`);
  html = html.replace(ITALIC_RE, (_, s) => `<em>${s}</em>`);
  // Hard line break: two trailing spaces.
  html = html.replace(/  \n/g, '<br />');
  return html;
};

const renderMarkdown = (md) => {
  if (!md) return [];
  // Normalise line endings, then split on blank lines into blocks.
  const blocks = String(md).replace(/\r\n/g, '\n').split(/\n{2,}/);
  return blocks.map((raw) => raw.trim()).filter(Boolean);
};

const Markdown = ({ source }) => {
  const blocks = renderMarkdown(source);
  return (
    <>
      {blocks.map((block, i) => {
        // Heading
        if (block.startsWith('## ')) {
          const text = block.replace(/^##\s+/, '');
          return (
            <h2
              key={i}
              dangerouslySetInnerHTML={{ __html: sanitize(renderInline(text)) }}
            />
          );
        }
        // Divider
        if (/^-{3,}$/.test(block)) {
          return <hr key={i} className="legal-divider" />;
        }
        // Callout (blockquote — used for the Terms italic note)
        if (block.startsWith('>')) {
          const text = block
            .split('\n')
            .map((l) => l.replace(/^>\s?/, ''))
            .join(' ')
            .trim();
          return (
            <div
              key={i}
              className="legal-callout"
              dangerouslySetInnerHTML={{ __html: sanitize(renderInline(text)) }}
            />
          );
        }
        // Paragraph — preserve single newlines as <br> only if explicit.
        const html = block
          .split('\n')
          .map((line) => renderInline(line))
          .join('<br />');
        return <p key={i} dangerouslySetInnerHTML={{ __html: sanitize(html) }} />;
      })}
    </>
  );
};

export default Markdown;
